import type {
  ConnectStripeDirectKeysRequest,
  StripeAccount,
} from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

import type { StripeDirectKeysValues } from './stripe-schemas';

/** A pending account (Stripe still reviewing) is shown like a connected one: it can be disconnected. */
export function hasStripeAccount(account: StripeAccount): boolean {
  return account.connected || account.status === 'pending';
}

/** 409 from connect: the platform has no Connect client id, so only the API-key route is open. */
export function isConnectUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'PAYMENT_PROVIDER_UNAVAILABLE';
}

export function isProviderRejected(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'PAYMENT_PROVIDER_REJECTED';
}

/** The backend keeps the Apple Pay domains in `capabilities` too; they are not a Stripe capability. */
const NOT_CAPABILITIES = new Set(['applePayDomains']);

/** Capabilities as Stripe reports them (`card_payments: "active"`), sorted, non-strings dropped. */
export function capabilityList(account: StripeAccount): Array<{ name: string; state: string }> {
  return Object.entries(account.capabilities)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .filter(([name]) => !NOT_CAPABILITIES.has(name))
    .map(([name, state]) => ({ name, state }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** An empty webhook secret is left out rather than sent as "". */
export function toDirectKeys(values: StripeDirectKeysValues): ConnectStripeDirectKeysRequest {
  return {
    secretKey: values.secretKey,
    publishableKey: values.publishableKey,
    ...(values.webhookSecret ? { webhookSecret: values.webhookSecret } : {}),
  };
}
